import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { withJWTClient } from "../connectClient";
import type { PreviewFillParams } from "./previewUserContext";
import type {
  TextComposerFrameVariantContentJson,
  TextComposerPreviewMedium,
} from "@/features/TextComposerV2/frame/frameOutput";
import type { FrameVariantConfig, SiteId } from "@/types/dragon-tiles";

import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const CONNECT_API_V1_URL: string =
  import.meta.env.VITE_CONNECT_API_V1_URL || "";

/** Request body for SMS/RCS/MMS on POST .../sites/:siteId/preview/test-send (Connect API v1). */
export type TextPreviewTestSendBody = {
  site_id: string;
  medium: TextComposerPreviewMedium;
  phone: string[];
  content_json: TextComposerFrameVariantContentJson;
  config: FrameVariantConfig;
  customerId: string;
  email?: string[];
  preview_fill_params?: PreviewFillParams;
};

export async function sendTextPreviewTestSend(
  siteId: SiteId,
  body: TextPreviewTestSendBody,
): Promise<void> {
  const { config, preview_fill_params: fillParams, ...rest } = body;
  const payload: Record<string, unknown> = { ...rest, config };
  if (fillParams) {
    payload.preview_fill_params = fillParams;
  }
  await withJWTClient((client) =>
    client.post(
      `${CONNECT_API_V1_URL}/sites/${siteId}/preview/test-send`,
      payload,
    ),
  );
}

export function useSendTextPreviewTestSend(siteId: SiteId) {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, TextPreviewTestSendBody>({
    mutationKey: ["sendTextPreviewTest", siteId],
    mutationFn: async (body) => {
      await sendTextPreviewTestSend(siteId, body);
    },
    onSuccess: () => {
      enqueueSnackbar("Test message sent successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to send test message");
    },
  });
}
